<script setup lang="ts">
import { p__ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import OzBadge, { OzBadgeColorScheme, OzBadgeSizePreset } from '@@/library/v4/components/OzBadge.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import type { LoginMethod } from '@@/pinia/auth_login_store'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    methods: LoginMethod[]
    shouldCachePreferredLoginMethod?: boolean
  }>(),
  {
    methods: () => [],
    shouldCachePreferredLoginMethod: false,
  },
)

function clickSAML(url: string | undefined, name: string): void {
  if (!url) return

  if (props.shouldCachePreferredLoginMethod) {
    safeLocalStorage.setItem('preferred_login_method', name)
  }
  navigateTo(url)
}

const preferredLoginMethod = computed((): string | null => {
  return safeLocalStorage.getItem('preferred_login_method')
})

function continueWithSamlText(methodName: string | undefined): string {
  return p__(
    'Text shown on SAML login button. SAML is a custom login method hosted by the university.',
    'Continue with %{samlLoginMethod}',
    { samlLoginMethod: methodName ?? 'SAML' },
  )
}

function isLastUsedMethod(method: LoginMethod): boolean {
  return method.name === preferredLoginMethod.value
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <OzContainedButton
      v-for="method in methods"
      :key="method.name"
      class="relative"
      data-testid="samlLoginButton"
      :color-scheme="OzContainedButtonColorScheme.AlternativeHeavy"
      :text="continueWithSamlText(method.displayName)"
      :center-text="true"
      @click="clickSAML(method.url, method.name)"
    >
      <template #leftIcon>
        <img v-if="method.logo" :src="method.logo" width="24" height="24" alt="" />
        <div v-else class="h-6 w-6"></div>
      </template>

      <OzBadge
        v-if="shouldCachePreferredLoginMethod && isLastUsedMethod(method)"
        class="absolute -top-1 end-4"
        :text="p__('Text shown as a badge above the most recently used login method', 'Last used')"
        :size-preset="OzBadgeSizePreset.Default"
        :color-scheme="OzBadgeColorScheme.SelectionInverse"
      />
    </OzContainedButton>
  </div>
</template>
